import { Link, useNavigate } from 'react-router-dom';
import { Star, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { optimizeImageUrl } from '@/lib/imageUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { PriceAlertModal } from '@/components/product/PriceAlertModal';
import { ChatCourseCard } from './ChatCourseCard';
import { requireUser } from '@/lib/requireUser';

interface ChatProductCardProps {
  product: {
    slug: string;
    title: string;
    subtitle?: string;
    image?: string;
    price?: number;
    msrp?: number;
    price_duration?: string;
    rating?: number;
    review_count?: number;
    categories?: string[];
    is_hot?: boolean;
    service_type?: string;
    profile_image?: string;
    instructor_info?: {
      name?: string;
      title?: string;
      avatar_url?: string;
    };
    total_video_hours?: number;
    certificate_included?: boolean;
    student_count?: number;
    skill_level?: string;
  };
}

export function ChatProductCard({ product }: ChatProductCardProps) {
  // If it's a course, use ChatCourseCard instead
  if (product.service_type === 'education') {
    return <ChatCourseCard product={product} />;
  }

  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAlertModal, setShowAlertModal] = useState(false);

  const handleSetAlert = (e: React.MouseEvent) => {
    e.preventDefault();
    requireUser({
      user,
      navigate,
      currentPath: window.location.pathname + window.location.search,
      action: () => setShowAlertModal(true),
      preferSignup: true,
    });
  };

  const displayPrice = product.price || 0;
  const formattedPrice = displayPrice === 0 ? 'Free' : `$${displayPrice}`;
  const duration = product.price_duration === 'mo' ? '/mo' : product.price_duration === 'yr' ? '/yr' : '';

  return (
    <>
      <Card className="overflow-hidden hover:shadow-md transition-shadow bg-card border-border">
        <Link to={`/product/${product.slug}`} className="block">
          <div className="flex gap-3 p-3">
            {/* Product Image */}
            <div className="flex-shrink-0 w-20 h-20 rounded-md overflow-hidden bg-muted">
              {product.image ? (
                <img
                  src={optimizeImageUrl(product.image, { width: 80, height: 80 })}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <span className="text-2xl">📦</span>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm leading-tight text-foreground truncate">
                    {product.title}
                  </h3>
                  {product.subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {product.subtitle}
                    </p>
                  )}
                </div>
                {product.is_hot && (
                  <Badge variant="destructive" className="text-xs px-1.5 py-0.5 shrink-0">
                    HOT
                  </Badge>
                )}
              </div>

              {/* Price & Rating */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-foreground">
                    {formattedPrice}
                  </span>
                  {duration && (
                    <span className="text-xs text-muted-foreground">{duration}</span>
                  )}
                </div>

                {product.rating && product.rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs font-medium text-foreground">
                      {product.rating.toFixed(1)}
                    </span>
                    {product.review_count && (
                      <span className="text-xs text-muted-foreground">
                        ({product.review_count})
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="flex gap-1.5 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs px-2"
                  onClick={handleSetAlert}
                >
                  <Bell className="w-3 h-3 mr-1" />
                  Price Alert
                </Button>
              </div>
            </div>
          </div>
        </Link>
      </Card>

      {showAlertModal && (
        <PriceAlertModal
          productId={product.slug}
          productTitle={product.title}
          currentPrice={product.price || 0}
          open={showAlertModal}
          onOpenChange={setShowAlertModal}
        />
      )}
    </>
  );
}
